/**
 * @module app.AppOverlay
 */
import olOverlay from 'ol/Overlay.js';

/**
 * @constructor
 * @param {olx.OverlayOptions} options
 * @param {app.CesiumLoader} appCesiumLoader
 * @extends {ol.Overlay}
 * @struct
 */
class AppOverlay extends olOverlay {
  constructor(options, appCesiumLoader) {
    options.cesium_ = appCesiumLoader;
    super(options);
    /**
    * @private
    */
    this.cesium_ = appCesiumLoader;

    /**
    * @type {Cesium.Cartesian3}
    * @private
    */
    this.click3DCartesian_ = null;
  }

  /**
  * @param {Cesium.Cartesian3} position
  */
  setCartesianPosition(position) {
    this.click3DCartesian_ = position;
  }

  /**
  * @override
  */
  updatePixelPosition() {
    const is3dEnabled = this.options.cesium_.is3dEnabled();
    const position = this.getPosition();
    if (!position) {
      this.setVisible(false);
      return;
    }

    if (!is3dEnabled) {
      super.updatePixelPosition();
    } else {
      const ol3d = this.options.cesium_.ol3d;
      const scene = ol3d.getCesiumScene();
      const cesiumCanvas = scene.canvas;
      /* There is a difference in the way OpenLayers and Cesium handle devicePixelRatio > 1.
      * - OL3 renders the map at highest resolution;
      * - On Safari Cesium does the same but on other browsers it renders the map at a smaller resolution then scales it.
      *
      * This is visible by looking at the clientWidth and width of the canvases.
      * Let't take an example:
      * - In browsers supporting image-rendering: pixelated
      *   - OL3     CW: 929px     W: 1858px
      *   - Cesium  CW: 929px     W: 929px
      * - On Safari (or when disabling image-rendering: pixelated)
      *   - OL3     CW: 929px     W: 1858px
      *   - Cesium  CW: 929px     W: 1858px
      *
      * We notice that the CW is the same for both libraries in all situations.
      * The width is smaller in Cesium on supported browsers.
      *
      * So what are canvas clientWidth and width?
      * - clientWidth is the DOM width of the element;
      * - width is the rendered buffer width of the canvas.
      *
      * The result of wgs84ToWindowCoordinates is in CW coordinates.
      *
      * The OL3 map size is in CW coordinates.
      *
      * In order to launch chromium with a custom pixel ratio. Use
      * chromium-browser --force-device-scale-factor=2
      */
      const csPixel = Cesium.SceneTransforms.wgs84ToWindowCoordinates(scene, this.click3DCartesian_);
      const pixel = [csPixel.x, csPixel.y];
      const mapSize = ol3d.getOlMap().getSize();
      console.assert(cesiumCanvas.clientWidth === mapSize[0]);
      console.assert(cesiumCanvas.clientHeight === mapSize[1]);
      this.updateRenderedPosition(pixel, mapSize);
    }
  }
}

export default AppOverlay;
